import React from 'react';
import { bool, node, oneOf, string } from 'prop-types';
import classNames from 'classnames';
import routeConfiguration from '../../routeConfiguration';
import { findRouteByRouteName } from '../../util/routes';
import useIsMounted from '../../util/useIsMounted';
import { IconSpinner, Icon } from '../../components';

import css from './Button.module.css';

/**
 * The template's Button, styled to the Components board. The family tokens (orange public, blue
 * dashboard) pick the colours, sizes and corners, so one PrimaryButton serves both.
 *
 * - `size`: 'large' (52 public), default (44 public / 40 dashboard), 'small' (36 / 34)
 * - `inProgress`: a spinner next to the label, and aria-busy
 * - `ready`: a checkmark
 * - `enforcePagePreloadFor`: a route name whose page chunk loads when the pointer is over it
 *
 * All buttons are disabled until the page is interactive, so nothing is submitted before the
 * browser handles it.
 */
const Button = props => {
  const {
    children,
    className,
    rootClassName,
    spinnerClassName,
    checkmarkClassName,
    readyClassName,
    inProgressClassName,
    checkmarkSize,
    size,
    inProgress,
    ready,
    disabled,
    enforcePagePreloadFor,
    ...rest
  } = props;
  const mounted = useIsMounted();

  const classes = classNames(rootClassName || css.root, className, css[size], {
    [css.ready]: ready,
    [css.inProgress]: inProgress,
    [readyClassName]: ready,
    [inProgressClassName]: inProgress,
  });

  let content;
  if (inProgress) {
    content = (
      <>
        <IconSpinner rootClassName={spinnerClassName || css.spinner} />
        {children}
      </>
    );
  } else if (ready) {
    content = (
      <Icon
        name="check"
        size={checkmarkSize === 'small' ? 20 : 24}
        rootClassName={checkmarkClassName || css.checkmark}
      />
    );
  } else {
    content = children;
  }

  const preloadPage = () => {
    // Loadable Component has a "preload" function.
    const { component: Page } = findRouteByRouteName(enforcePagePreloadFor, routeConfiguration());
    if (Page.preload) {
      Page.preload();
    }
  };
  const preloadMaybe = enforcePagePreloadFor
    ? { onMouseOver: preloadPage, onTouchStart: preloadPage }
    : {};

  return (
    <button
      className={classes}
      {...preloadMaybe}
      aria-busy={inProgress ? 'true' : undefined}
      {...rest}
      disabled={mounted ? disabled : true}
    >
      {content}
    </button>
  );
};

Button.defaultProps = {
  rootClassName: null,
  className: null,
  spinnerClassName: null,
  checkmarkClassName: null,
  readyClassName: null,
  inProgressClassName: null,
  checkmarkSize: null,
  size: 'default',
  inProgress: false,
  ready: false,
  disabled: false,
  enforcePagePreloadFor: null,
  children: null,
};

Button.propTypes = {
  rootClassName: string,
  className: string,
  spinnerClassName: string,
  checkmarkClassName: string,
  readyClassName: string,
  inProgressClassName: string,
  checkmarkSize: string,
  size: oneOf(['large', 'default', 'small']),

  inProgress: bool,
  ready: bool,
  disabled: bool,
  enforcePagePreloadFor: string,

  children: node,
};

export default Button;

const variant = (rootClass, handleClass, displayName) => {
  const Variant = props => {
    const classes = classNames(props.rootClassName || rootClass, handleClass);
    return <Button {...props} rootClassName={classes} />;
  };
  Variant.displayName = displayName;
  return Variant;
};

export const PrimaryButton = variant(css.primaryButtonRoot, css.primaryButton, 'PrimaryButton');
export const PrimaryButtonInline = variant(
  css.primaryButtonInlineRoot,
  css.primaryButton,
  'PrimaryButtonInline'
);
export const SecondaryButton = variant(
  css.secondaryButtonRoot,
  css.secondaryButton,
  'SecondaryButton'
);
export const SecondaryButtonInline = variant(
  css.secondaryButtonInlineRoot,
  css.secondaryButton,
  'SecondaryButtonInline'
);
export const DangerButton = variant(css.dangerButtonRoot, css.dangerButton, 'DangerButton');
export const InlineTextButton = variant(
  css.inlineTextButtonRoot,
  css.inlineTextButton,
  'InlineTextButton'
);
export const SocialLoginButton = variant(
  css.socialButtonRoot,
  css.socialButton,
  'SocialLoginButton'
);
